import { useState, useRef, useEffect } from 'react';
import { useKasa } from '../../context/KasaContext';
import DatePicker from '../../components/DatePicker';
import { GelirEkleModal } from '../../components/KasaModals';
import ExcelImportModal from '../../components/ExcelImportModal';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import OnayPopup from '../../components/OnayPopup';
import { formatPara } from '../../utils/formatUtils';
import Pagination from '../../components/Pagination';
import ActionButtons from '../../components/ActionButtons';

// ── Sabitler ──
function pad(n) { return String(n).padStart(2, '0'); }
function bugunStr() {
  var d = new Date();
  return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function ayinBasiStr() {
  var d = new Date(); d.setDate(1);
  return pad(1) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function ddmmyyyyToDate(s) {
  if (!s) return null;
  var p = s.split('.');
  if (p.length !== 3) return null;
  return new Date(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0]));
}

// ── Searchable Select (filtre için) ──
function SearchableSelect({ value, onChange, options, placeholder }) {
  var [open, setOpen] = useState(false);
  var [search, setSearch] = useState('');
  var ref = useRef();
  useEffect(function () {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);
  var filtered = options.filter(function (o) { return o.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')); });
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={function () { setOpen(function (v) { return !v; }); setSearch(''); }}
        className="w-full flex items-center justify-between border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold bg-white hover:border-blue-400 focus:outline-none transition-colors shadow-sm focus:ring-4 focus:ring-blue-50">
        <span className={value === 'Hepsi' || !value ? 'text-gray-400 uppercase tracking-widest text-[10px] font-black' : 'text-gray-800 uppercase tracking-widest text-[10px] font-black truncate'}>{value || placeholder || 'Hepsi'}</span>
        <span className="text-gray-300 ml-2 text-[10px]">▼</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl min-w-[140px] max-h-52 overflow-y-auto w-full p-2 animate-scale-in origin-top">
          <div className="pb-2 mb-2 border-b border-gray-50">
            <input autoFocus value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder="Ara..."
              className="w-full bg-gray-50 border-none rounded-xl px-3 py-2 text-xs font-bold focus:ring-2 focus:ring-blue-100 focus:outline-none uppercase tracking-widest" />
          </div>
          {filtered.map(function (o) {
            return (
              <div key={o} onClick={function () { onChange(o); setOpen(false); }}
                className={'px-3 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl cursor-pointer transition-colors ' + (value === o ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-600')}>
                {o}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Mobil kart bileşeni ──
function GelirKart({ g, onClick }) {
  return (
    <div className="group relative bg-white border-2 border-gray-100 rounded-[2.5rem] p-6 transition-all duration-500 shadow-sm hover:shadow-xl hover:border-emerald-100 hover:-translate-y-1 transform-gpu cursor-pointer text-left leading-none flex flex-col" onClick={onClick}>
      <div className="flex items-start justify-between mb-5 gap-2">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest opacity-80">{g.tarihSaat}</span>
          <span className="text-sm md:text-base font-black text-gray-800 tracking-tight leading-none group-hover:text-emerald-600 transition-colors truncate whitespace-normal break-words">{g.ad || '—'}</span>
        </div>
        <span className="text-sm md:text-base font-black text-emerald-600 whitespace-nowrap tracking-tighter">
          {formatPara(g.tutar || 0)} <span className="text-[10px] uppercase">{g.paraBirimi}</span>
        </span>
      </div>
      <div className="mt-auto pt-4 border-t border-gray-50 flex flex-wrap gap-2 text-[9px] font-black uppercase tracking-widest">
        <span className="px-3 py-1.5 rounded-xl bg-gray-50 text-gray-500 border border-gray-100">{g.grup}</span>
        <span className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">{g.odemeTip}</span>
        {g.personel && <span className="px-3 py-1.5 rounded-xl bg-purple-50 text-purple-600 border border-purple-100">{g.personel}</span>}
      </div>
    </div>
  );
}

// ── Ana Sayfa ──
export default function GelirListesi() {
  var bugun = bugunStr();
  var ayBasi = ayinBasiStr();

  var [baslar, setBaslar] = useState(ayBasi);
  var [bitis, setBitis] = useState(bugun);
  var [grup, setGrup] = useState('Hepsi');
  var [personel, setPersonel] = useState('Hepsi');
  var [odemeTip, setOdemeTip] = useState('Hepsi');
  var [adAciklama, setAdAciklama] = useState('');
  var [filtreKey, setFiltreKey] = useState({ baslar: ayBasi, bitis: bugun, grup: 'Hepsi', personel: 'Hepsi', odemeTip: 'Hepsi', ad: '' });

  var [colTarih, setColTarih] = useState('');
  var [colAd, setColAd] = useState('');
  var [colGrup, setColGrup] = useState('');
  var [colOdeme, setColOdeme] = useState('');
  var [colTutar, setColTutar] = useState('');
  var [colPersonel, setColPersonel] = useState('');

  var [sortKol, setSortKol] = useState('tarihSaat');
  var [sortDir, setSortDir] = useState('desc');
  var [aktifSayfa, setAktifSayfa] = useState(1);
  var [sayfaBasi, setSayfaBasi] = useState(100);

  var { gelirler: ctxGelirler, gelirEkle, gelirGuncelle, gelirSil, odemeSil,
    gelirGruplari, giderGruplari, giderEkle,
    personeller, odemeTipleri, bankalar, paraBirimleri, dovizKurlari,
    fetchVeriler } = useKasa();
  var [modal, setModal] = useState(null);
  var [excelModal, setExcelModal] = useState(false);
  var [silOnay, setSilOnay] = useState(null);

  useEffect(function () { fetchVeriler(); }, []); // eslint-disable-line
  useEffect(function () { setAktifSayfa(1); }, [colTarih, colAd, colGrup, colOdeme, colTutar, colPersonel]);

  var grupFilter = ['Hepsi', ...Array.from(new Set(ctxGelirler.map(function (g) { return g.grup || ''; }).filter(Boolean))).sort()];
  var personelFilter = ['Hepsi', ...(personeller || []).map(function (p) { return p.adSoyad; })];
  var odemeTipFilter = ['Hepsi', ...(odemeTipleri || []).map(function (o) { return o.ad || o; })];

  var gelirler = ctxGelirler.map(function (g) {
    if (g.tarihSaat) return g;
    return Object.assign({}, g, { tarihSaat: (g.tarih || '') + ' ' + (g.saat || '') });
  });

  function filtrele() {
    setAktifSayfa(1);
    setFiltreKey({ baslar, bitis, grup, personel, odemeTip, ad: adAciklama });
  }
  function temizle() {
    setAktifSayfa(1);
    setBaslar(ayBasi); setBitis(bugun);
    setGrup('Hepsi'); setPersonel('Hepsi'); setOdemeTip('Hepsi'); setAdAciklama('');
    setFiltreKey({ baslar: ayBasi, bitis: bugun, grup: 'Hepsi', personel: 'Hepsi', odemeTip: 'Hepsi', ad: '' });
    setColTarih(''); setColAd(''); setColGrup(''); setColOdeme(''); setColTutar(''); setColPersonel('');
  }

  function sortBy(kol) {
    if (sortKol === kol) setSortDir(function (d) { return d === 'asc' ? 'desc' : 'asc'; });
    else { setSortKol(kol); setSortDir('asc'); }
  }
  function sortIkon(kol) {
    if (sortKol !== kol) return <span className="text-gray-300 ml-1 opacity-50">⇅</span>;
    return <span className="text-emerald-500 ml-1 font-black">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  var liste = gelirler.filter(function (g) {
    var gTarih = ddmmyyyyToDate(g.tarihSaat.split(' ')[0]);
    var fBas = ddmmyyyyToDate(filtreKey.baslar); if (fBas) fBas.setHours(0, 0, 0, 0);
    var fBit = ddmmyyyyToDate(filtreKey.bitis); if (fBit) fBit.setHours(23, 59, 59, 999);
    if (gTarih) gTarih.setHours(0, 0, 0, 0);
    if (fBas && gTarih && gTarih < fBas) return false;
    if (fBit && gTarih && gTarih > fBit) return false;
    if (filtreKey.grup !== 'Hepsi' && g.grup !== filtreKey.grup) return false;
    if (filtreKey.personel !== 'Hepsi' && g.personel !== filtreKey.personel) return false;
    if (filtreKey.odemeTip !== 'Hepsi' && g.odemeTip !== filtreKey.odemeTip) return false;
    if (filtreKey.ad && !g.ad.toLocaleLowerCase('tr-TR').includes(filtreKey.ad.toLocaleLowerCase('tr-TR'))) return false;
    if (colTarih && !g.tarihSaat.includes(colTarih)) return false;
    if (colAd && !g.ad.toLocaleLowerCase('tr-TR').includes(colAd.toLocaleLowerCase('tr-TR'))) return false;
    if (colGrup && !g.grup.toLocaleLowerCase('tr-TR').includes(colGrup.toLocaleLowerCase('tr-TR'))) return false;
    if (colOdeme && !g.odemeTip.toLocaleLowerCase('tr-TR').includes(colOdeme.toLocaleLowerCase('tr-TR'))) return false;
    if (colTutar && !String(g.tutar).includes(colTutar)) return false;
    if (colPersonel && !g.personel.toLocaleLowerCase('tr-TR').includes(colPersonel.toLocaleLowerCase('tr-TR'))) return false;
    return true;
  }).sort(function (a, b) {
    var av = a[sortKol]; var bv = b[sortKol];
    if (sortKol === 'tutar') { av = parseFloat(av); bv = parseFloat(bv); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  var listePage = liste.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi);

  var ozet = {};
  liste.forEach(function (g) {
    var tip = (g.odemeTip || 'DİĞER').toLocaleUpperCase('tr');
    if (!ozet[tip]) ozet[tip] = 0;
    ozet[tip] += (g.tutar || 0) * (g.kurDegeri || 1);
  });
  var genelToplam = liste.reduce(function (acc, g) { return acc + (g.tutar || 0) * (g.kurDegeri || 1); }, 0);

  async function handleKaydet(data) {
    if (data.kasaGelirId || data.id) { await gelirGuncelle(data); }
    else { await gelirEkle(data); }
  }

  var INP_COL = 'w-full border-2 border-gray-100 rounded-xl px-3 py-2 text-[10px] font-bold focus:outline-none focus:border-blue-400 bg-white placeholder-gray-300 uppercase tracking-widest shadow-sm';

  function handleSatirSil(g) {
    setSilOnay(g);
  }
  async function handleSilOnayla() {
    if (!silOnay) return;
    const id = silOnay.id;
    if (String(id).startsWith('odeme-')) await odemeSil(String(id).replace('odeme-', ''));
    else await gelirSil(id);
    setSilOnay(null);
  }

  var infoCards = [
    { label: 'TOPLAM GELİR', value: formatPara(genelToplam, 'TL'), icon: '📈', color: 'from-emerald-500 to-teal-600 shadow-emerald-100' },
    { label: 'KAYIT SAYISI', value: liste.length, icon: '📋', color: 'from-blue-500 to-indigo-600 shadow-blue-100' }
  ];
  const _ozetRenk = ['from-indigo-500 to-purple-600 shadow-indigo-100','from-orange-400 to-rose-500 shadow-orange-100','from-sky-500 to-cyan-600 shadow-sky-100','from-violet-500 to-purple-700 shadow-violet-100'];
  Object.entries(ozet).forEach(([k, v], i) => {
    infoCards.push({ label: k + ' GELİRİ', value: formatPara(v, 'TL'), icon: '💳', color: _ozetRenk[i % _ozetRenk.length] });
  });

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn leading-none" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="💰"
        iconColor="from-emerald-500 to-teal-700"
        iconShadow="shadow-emerald-200"
        title="Gelir Listesi"
        subtitle="Kasa gelirleri ve tahsilat takibi"
      >
        <div className="flex flex-wrap gap-3">
          <button onClick={() => downloadExcel('gelir-listesi', 'Gelirler', ['Tarih/Saat','Açıklama','Kategori','Ödeme Tipi','Tutar','Para Birimi','Personel'], liste.map(g => [g.tarihSaat, g.ad, g.grup||'—', g.odemeTip||'—', (g.tutar||0).toFixed(2), g.paraBirimi||'TL', g.personel||'—']))}
            className="flex-shrink-0 flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 border-2 border-emerald-100 hover:border-emerald-400 hover:bg-emerald-100 px-6 py-4 rounded-2xl text-[10px] font-black transition-all active:scale-95 uppercase tracking-widest leading-none shadow-sm">
            📊 EXCEL DIŞA AKTAR
          </button>
          <button onClick={() => setExcelModal(true)}
            className="flex-shrink-0 flex items-center justify-center gap-2 bg-white text-gray-700 border-2 border-gray-100 hover:border-gray-200 hover:bg-gray-50 px-6 py-4 rounded-2xl text-[10px] font-black transition-all active:scale-95 uppercase tracking-widest leading-none shadow-sm">
            📥 EXCEL İÇE AKTAR
          </button>
          <button onClick={() => setModal('ekle')}
            className="group flex-shrink-0 flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-4 rounded-2xl text-[10px] font-black shadow-xl shadow-emerald-100 transition-[background-color,transform] duration-300 hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden leading-none">
            <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
            <span className="relative text-xl transition-transform group-hover:rotate-90 leading-none">＋</span>
            <span className="relative">YENİ GELİR EKLE</span>
          </button>
        </div>
      </PageHeader>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 leading-none">
        {infoCards.map((k, i) => (
          <div key={i} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-5 text-white shadow-xl hover:shadow-2xl transition-all duration-300 group`}>
            <div className="absolute -right-4 -bottom-4 text-6xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-500 transform-gpu">{k.icon}</div>
            <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none text-left">{k.label}</p>
            <p className="text-xl md:text-2xl font-black tracking-tight leading-none text-left">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[3rem] border border-gray-200 shadow-sm overflow-hidden min-h-[500px] flex flex-col">
        {/* Filtre satırı */}
        <div className="px-6 md:px-8 py-6 border-b border-gray-100 bg-gray-50/30 flex flex-col xl:flex-row gap-6">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 w-full text-left">
            <div className="relative">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10 drop-shadow-sm">Başlangıç</label>
              <DatePicker value={baslar} onChange={setBaslar} />
            </div>

            <div className="relative">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10 drop-shadow-sm">Bitiş</label>
              <DatePicker value={bitis} onChange={setBitis} />
            </div>

            <div className="relative">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10 drop-shadow-sm">Grup</label>
              <SearchableSelect value={grup} onChange={setGrup} options={grupFilter} />
            </div>

            <div className="relative">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10 drop-shadow-sm">Personel</label>
              <SearchableSelect value={personel} onChange={setPersonel} options={personelFilter} />
            </div>

            <div className="relative">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10 drop-shadow-sm">Ödeme Tipi</label>
              <SearchableSelect value={odemeTip} onChange={setOdemeTip} options={odemeTipFilter} />
            </div>

            <div className="relative">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10 drop-shadow-sm">Arama</label>
              <input value={adAciklama} onChange={e => setAdAciklama(e.target.value)} placeholder="Adı veya Açıklama..."
                className="w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-[10px] font-black tracking-widest uppercase bg-white focus:outline-none focus:border-blue-400 shadow-sm" />
            </div>
          </div>

          <div className="flex items-center justify-between xl:justify-end gap-3 w-full xl:w-auto ml-auto leading-none">
            <button onClick={temizle} className="px-6 py-4 text-[10px] font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 rounded-2xl transition-all active:scale-95 leading-none shadow-sm border-2 border-rose-50">SIFIRLA</button>
            <button onClick={filtrele} className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-blue-200 transition-all uppercase tracking-widest active:scale-95 leading-none transform-gpu">FİLTRELE</button>
          </div>
        </div>

        {/* Listeler */}
        <div className="p-6 md:p-8 flex-1 bg-white">
          {/* Mobil kart görünümü */}
          <div className="block lg:hidden space-y-4">
            {liste.length === 0
              ? <EmptyState text="Kayıt bulunamadı." />
              : listePage.map(g => <GelirKart key={g.id} g={g} onClick={() => setModal(g)} />)
            }
          </div>

          {/* Masaüstü tablo */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-100">
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-colors rounded-tl-2xl" onClick={() => sortBy('tarihSaat')}>Tarih/Saat {sortIkon('tarihSaat')}</th>
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => sortBy('ad')}>Adı / Açıklama {sortIkon('ad')}</th>
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => sortBy('grup')}>Grup {sortIkon('grup')}</th>
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => sortBy('odemeTip')}>Ödeme Tip {sortIkon('odemeTip')}</th>
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-colors text-right" onClick={() => sortBy('tutar')}>Tutar {sortIkon('tutar')}</th>
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => sortBy('personel')}>Personel {sortIkon('personel')}</th>
                  <th className="px-4 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-24 rounded-tr-2xl">İşlem</th>
                </tr>
                <tr className="bg-gray-50/50">
                  <td className="p-2"><input value={colTarih} onChange={e => setColTarih(e.target.value)} placeholder="Filtrele" className={INP_COL} /></td>
                  <td className="p-2"><input value={colAd} onChange={e => setColAd(e.target.value)} placeholder="Filtrele" className={INP_COL} /></td>
                  <td className="p-2"><input value={colGrup} onChange={e => setColGrup(e.target.value)} placeholder="Filtrele" className={INP_COL} /></td>
                  <td className="p-2"><input value={colOdeme} onChange={e => setColOdeme(e.target.value)} placeholder="Filtrele" className={INP_COL} /></td>
                  <td className="p-2"><input value={colTutar} onChange={e => setColTutar(e.target.value)} placeholder="Filtrele" className={INP_COL} /></td>
                  <td className="p-2"><input value={colPersonel} onChange={e => setColPersonel(e.target.value)} placeholder="Filtrele" className={INP_COL} /></td>
                  <td></td>
                </tr>
              </thead>
              <tbody className="text-xs font-bold text-gray-700">
                {liste.length === 0 ? (
                  <EmptyState as="td" colSpan={7} text="Kayıt bulunamadı" />
                ) : listePage.map(function (g) {
                  return (
                    <tr key={g.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors group">
                      <td className="px-4 py-4 whitespace-nowrap opacity-70 font-mono tracking-tighter">{g.tarihSaat}</td>
                      <td className="px-4 py-4 group-hover:text-blue-600 transition-colors">{g.ad}</td>
                      <td className="px-4 py-4">
                        {g.grup === 'ADİSYON ÖDEMESİ'
                          ? <span className="bg-orange-50 border border-orange-100 text-orange-600 text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest">🍽 Adisyon</span>
                          : g.grup === 'KONAKLAMA'
                            ? <span className="bg-blue-50 border border-blue-100 text-blue-600 text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest">🏨 Konaklama</span>
                            : <span className="bg-gray-100 border border-gray-200 text-gray-600 text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest">{g.grup}</span>}
                      </td>
                      <td className="px-4 py-4 opacity-80">{g.odemeTip}</td>
                      <td className="px-4 py-4 text-right text-sm font-black text-emerald-600">
                        {formatPara(g.tutar)} <span className="text-[9px] opacity-70">{g.paraBirimi}</span>
                        {g.paraBirimi !== 'TL' && g.paraBirimi !== 'TRY' && (g.kurDegeri || 0) > 0 && (
                          <div className="text-[9px] text-blue-500 font-black opacity-80 tracking-widest mt-1">{formatPara((g.tutar || 0) * (g.kurDegeri || 1))} TL</div>
                        )}
                      </td>
                      <td className="px-4 py-4 opacity-80">{g.personel}</td>
                      <td className="px-4 py-4">
                        <ActionButtons onEdit={() => setModal(g)} onDelete={() => handleSatirSil(g)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination toplam={liste.length} sayfaBasi={sayfaBasi} sayfaBasiDegistir={setSayfaBasi} aktifSayfa={aktifSayfa} sayfaDegistir={setAktifSayfa} />
        </div>
      </div>

      {/* Excel İçe Aktar Modal */}
      {excelModal && (
        <ExcelImportModal
          onClose={() => setExcelModal(false)}
          gelirEkle={gelirEkle}
          giderEkle={giderEkle}
          gelirGruplari={gelirGruplari || []}
          giderGruplari={giderGruplari || []}
          odemeTipleri={odemeTipleri || []}
          bankalar={bankalar || []}
        />
      )}

      {/* Modal */}
      {modal && (
        <GelirEkleModal
          mevcutKayit={modal === 'ekle' ? null : modal}
          onClose={() => setModal(null)}
          onKaydet={handleKaydet}
          onSil={(id) => {
            if (String(id).startsWith('odeme-')) return odemeSil(String(id).replace('odeme-', ''));
            return gelirSil(id);
          }}
          gelirGruplari={gelirGruplari || []}
          personeller={personeller || []}
          odemeTipleri={(odemeTipleri || []).map(o => o.ad || o)}
          bankalar={(bankalar || []).map(b => b.ad || b)}
          paraBirimleri={(paraBirimleri || []).map(p => p.tcmbKod || p.ad || p)}
          dovizKurlari={dovizKurlari || {}}
        />
      )}

      {/* Silme Onay Popup */}
      <OnayPopup
        acik={!!silOnay}
        baslik="Gelir Kaydını Sil"
        mesaj={silOnay ? `"${silOnay.ad || 'Bu kayıt'}" silinecek. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?` : ''}
        onEvet={handleSilOnayla}
        onHayir={() => setSilOnay(null)}
      />

      <UsageInfo
        color="emerald"
        description="Gelir Listesi, tesisinizdeki tüm kasa girişlerini (nakit, kredi kartı, havale vb.) ve rezervasyon tahsilatlarını tek bir yerden izlemenizi sağlar."
        scenarios={[
          { title: "Tahsilat Takibi", content: "Günlük kasa girişlerini, adisyon ödemelerini ve rezervasyon kaparo/bakiyelerini anlık olarak kaydedebilirsiniz." },
          { title: "Finansal Analiz", content: "Gelir gruplarına, ödeme tiplerine veya işlem yapan personele göre filtreleme yaparak detaylı gelir raporları oluşturabilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scale-in 0.3s ease-out forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
